import { i18n } from '../../i18n/i18n'
import { type MusicGroup } from '../types/musicGroup/musicGroup.type'
import { type ProgEvent } from '../types/progEvent.type'

export const ProgEventUtils = {
  getMeanRateDisplay(meanRate: number): string {
    if (isNaN(meanRate) || meanRate === -1) {
      return '-'
    }
    return (Math.round((meanRate + Number.EPSILON) * 100) / 100).toString()
  },

  hasHiddenGuests(
    item?: ProgEvent | null,
    storeGuests?: Pick<MusicGroup, '_id' | 'showArtist'>[],
  ): boolean {
    return (item?.musicGroups ?? []).some((g: MusicGroup) => {
      if (storeGuests?.length) {
        const storeGuest = storeGuests.find((sg) => sg._id === g._id)
        if (storeGuest) return !storeGuest.showArtist
      }
      return !g.showArtist
    })
  },

  getHiddenGuestsTooltip(
    item: ProgEvent | null | undefined,
    storeGuests?: Pick<MusicGroup, '_id' | 'showArtist'>[],
  ): string {
    const hiddenGuests = (item?.musicGroups ?? []).filter((g: MusicGroup) => {
      if (storeGuests?.length) {
        const storeGuest = storeGuests.find((sg) => sg._id === g._id)
        if (storeGuest) return !storeGuest.showArtist
      }
      return !g.showArtist
    })
    if (hiddenGuests.length === 0) return ''

    const allHidden = item?.musicGroups?.length === hiddenGuests.length
    if (allHidden) {
      return i18n.tc(
        'PROGRAMMATION.WARNINGS.ALL_GUESTS_HIDDEN',
        hiddenGuests.length,
      )
    }

    const names = hiddenGuests
      .map((g: MusicGroup) => g.names?.fr || g.names?.en || '')
      .filter(Boolean)
      .join(', ')
    return i18n.tc(
      'PROGRAMMATION.WARNINGS.SOME_GUESTS_HIDDEN',
      hiddenGuests.length,
      {
        names,
      },
    )
  },
}

/**
 * Normalizes a string by converting to lower case and removing diacritics (accents).
 * Used for accent-insensitive search filtering.
 */
export const normalizeStringForSearch = (
  str: string | null | undefined,
): string => {
  if (!str) return ''
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

/**
 * Custom filter for Vuetify v-autocomplete and v-combobox to ignore accents.
 */
export const areSameTagNames = (
  a?: { fr?: string | null; en?: string | null } | null,
  b?: { fr?: string | null; en?: string | null } | null,
): boolean => {
  if (!a || !b) return false
  const normalize = (s?: string | null) => normalizeStringForSearch(s?.trim())
  const aFr = normalize(a.fr)
  const aEn = normalize(a.en)
  const bFr = normalize(b.fr)
  const bEn = normalize(b.en)

  if (aFr && bFr && aFr !== bFr) return false
  if (aEn && bEn && aEn !== bEn) return false
  return !!(aFr && bFr) || !!(aEn && bEn)
}

export const isSupersetTagNames = (
  a?: { fr?: string | null; en?: string | null } | null,
  b?: { fr?: string | null; en?: string | null } | null,
): boolean => {
  if (!a || !b) return false
  const normalize = (s?: string | null) => normalizeStringForSearch(s?.trim())
  return (
    (!normalize(b.fr) || !!normalize(a.fr)) &&
    (!normalize(b.en) || !!normalize(a.en))
  )
}

export const isConflictTagNames = (
  a?: { fr?: string | null; en?: string | null } | null,
  b?: { fr?: string | null; en?: string | null } | null,
): boolean => {
  if (!a || !b) return false
  const normalize = (s?: string | null) => normalizeStringForSearch(s?.trim())
  const aFr = normalize(a.fr)
  const aEn = normalize(a.en)
  const bFr = normalize(b.fr)
  const bEn = normalize(b.en)
  return !!(
    (aFr && bFr && aFr === bFr && aEn && bEn && aEn !== bEn) ||
    (aEn && bEn && aEn === bEn && aFr && bFr && aFr !== bFr)
  )
}

export const mergeTagNames = (
  a?: { fr?: string | null; en?: string | null } | null,
  b?: { fr?: string | null; en?: string | null } | null,
): { fr: string; en: string } => {
  const aFr = a?.fr?.trim() ?? ''
  const aEn = a?.en?.trim() ?? ''
  return {
    fr: aFr || (b?.fr ?? ''),
    en: aEn || (b?.en ?? ''),
  }
}

export const vuetifyAccentInsensitiveFilter = (
  item: unknown,
  queryText: string,
  itemText: string,
): boolean => {
  return normalizeStringForSearch(itemText).includes(
    normalizeStringForSearch(queryText),
  )
}
